import { z } from 'zod';
import { FederalState } from '../enums.js';
/**
 * API-Vertrag für die Betreiber-Konfiguration der fachlichen Prüfengine.
 *
 * Bewusst NICHT enthalten: ein Weg, Schwellenwerte zu ändern. 22 m Hochhaus und
 * 35 m Rettungsweg sind geltendes Recht, keine Einstellung. Sie werden nur
 * ausgeliefert, damit die Oberfläche sie anzeigen kann — schreibgeschützt, mit
 * Quelle und Prüfdatum. Wer sie ändern will, ändert `data/state-rules.json`
 * mit neuem `lastVerified`.
 */
export const ruleSeverity = z.enum(['error', 'warning', 'info']);
export const ruleEvidence = z.enum(['belegt', 'ungeprueft', 'gemischt']);
/** Eine Regel im Admin-Bereich: Default, aktueller Stand und Herkunft. */
export const ruleSettingDto = z.object({
    code: z.string().min(1).max(80),
    label: z.string(),
    description: z.string(),
    /** Aktuell wirksam. */
    enabled: z.boolean(),
    severity: ruleSeverity,
    /** Begründung, falls vom Default abgewichen wurde. */
    note: z.string().nullable(),
    /** Werkseinstellung — die Oberfläche zeigt Abweichungen davon an. */
    defaultSeverity: ruleSeverity,
    /** true, wenn Betreiber-Einstellungen den Default überschreiben. */
    overridden: z.boolean(),
    evidence: ruleEvidence,
    /** Schlüssel der Schwellenwerte, gegen die diese Regel prüft. */
    thresholdKeys: z.array(z.string()),
    updatedAt: z.string().datetime().nullable(),
    updatedByName: z.string().nullable(),
});
/** Schwellenwerte eines Bundeslands — reine Anzeige. */
export const stateThresholdsDto = z.object({
    state: FederalState.nullable(),
    lboAbbr: z.string(),
    /**
     * Nur die Werte, die das Land tatsächlich kennt. Ein fehlender Schlüssel
     * heißt: die zugehörige Prüfung ist dort ausgesetzt.
     */
    values: z.record(z.string(), z.number()),
    /**
     * Begründung je Feldschlüssel — warum ein Wert fehlt oder von der
     * Musterbauordnung abweicht. Ohne sie sähe eine Lücke wie ein Defekt aus.
     */
    hinweise: z.record(z.string(), z.string()),
});
export const rulesConfigResponse = z.object({
    settings: z.array(ruleSettingDto),
    /** Beschriftungen für die Schwellenwert-Tabelle. */
    thresholdLabels: z.record(z.string(), z.string()),
    /** Je Bundesland die geltenden Werte, plus MBO-Default (state: null). */
    thresholds: z.array(stateThresholdsDto),
    /** Version und Prüfdatum der Regeldatenbasis — gehört sichtbar ins UI. */
    dataVersion: z.string(),
    dataLastVerified: z.string(),
});
// ============================================================================
// Abweichungen (§ 67 MBO / Art. 63 BayBO)
// ============================================================================
export const deviationStatus = z.enum(['begruendet', 'akzeptiert', 'zurueckgewiesen']);
/**
 * Begründung einer Abweichung, die der Planer beim Einreichen mitgibt.
 *
 * Das ist kein Formalismus der App: ein Brandschutzkonzept muss Abweichungen
 * ohnehin auflisten — mit Fundstelle, Ist-Wert und Kompensationsmaßnahme.
 * Was hier eingegeben wird, landet später im Kapitel „Abweichungen und
 * Kompensationsmaßnahmen" des generierten Dokuments. Der Planer schreibt es
 * also nicht doppelt.
 */
export const deviationJustification = z.object({
    code: z.string().min(1).max(80),
    justification: z
        .string()
        .trim()
        .min(10, 'Bitte die Kompensationsmaßnahme oder Begründung beschreiben (mind. 10 Zeichen).')
        .max(2000),
});
export const submitProjectRequest = z.object({
    /** Je offener Warnung genau eine Begründung. */
    deviations: z.array(deviationJustification).max(50).optional(),
});
// ============================================================================
// Reifegrad: was Einreichen und Abschluss blockiert
// ============================================================================
/**
 * Ein offener Punkt — fehlende Pflichtangabe oder unbestätigter Feldvorschlag.
 *
 * `label` kommt vom Server mit, damit die Oberfläche keine zweite Liste
 * deutscher Feldnamen pflegen muss; `field` ist der Feldschlüssel und taugt
 * als Sprungmarke ins Formular.
 */
export const readinessIssueDto = z.object({
    field: z.string(),
    label: z.string(),
    message: z.string(),
});
/**
 * Antwort von `GET /projects/:id/readiness`.
 *
 * Damit zeigt der Einreichen-Dialog dieselbe Sperre VOR dem Absenden an, die
 * der Server danach durchsetzen würde. Die Sperre selbst liegt am Endpoint —
 * die Anzeige ist Höflichkeit, keine Durchsetzung (CLAUDE.md Regel #5).
 */
export const projectReadinessResponse = z.object({
    /** Pflichtangaben, die fehlen oder nur als unbestätigter Vorschlag vorliegen. */
    missingRequiredFields: z.array(readinessIssueDto),
    /** Feldvorschläge (KI, Klon, Dokument) ohne Entscheidung des Planers. */
    openProposals: z.array(readinessIssueDto),
    /** Einreichen scheitert an fehlenden Pflichtangaben. */
    canSubmit: z.boolean(),
    /** Abschluss scheitert zusätzlich an unbestätigten Vorschlägen. */
    canComplete: z.boolean(),
});
export const ruleDeviationDto = z.object({
    id: z.number().int().positive(),
    code: z.string(),
    fields: z.array(z.string()),
    message: z.string(),
    legalRef: z.string(),
    severity: ruleSeverity,
    justification: z.string(),
    status: deviationStatus,
    submittedByName: z.string(),
    submittedAt: z.string().datetime(),
    decidedByName: z.string().nullable(),
    decidedAt: z.string().datetime().nullable(),
    decisionNote: z.string().nullable(),
    /** Gesetzt, wenn der Befund inzwischen behoben ist. */
    resolvedAt: z.string().datetime().nullable(),
});
/** Entscheidung des Freigebers über eine begründete Abweichung. */
export const decideDeviationRequest = z
    .object({
    status: z.enum(['akzeptiert', 'zurueckgewiesen']),
    decisionNote: z.string().trim().max(1000).nullable().optional(),
})
    .refine((v) => v.status === 'akzeptiert' || (v.decisionNote != null && v.decisionNote.trim().length >= 5), {
    message: 'Beim Zurückweisen ist eine Begründung erforderlich (mind. 5 Zeichen).',
    path: ['decisionNote'],
});
/**
 * Änderung einer Regel-Einstellung. `note` ist Pflicht, sobald abgeschaltet
 * wird: eine stillschweigend entschärfte Prüfung ist gefährlicher als gar
 * keine, weil sie Sicherheit vortäuscht. Die Begründung landet im Audit-Log
 * und wird im Projekt neben „Regeln deaktiviert" angezeigt.
 */
export const updateRuleSettingRequest = z
    .object({
    enabled: z.boolean(),
    severity: ruleSeverity,
    note: z.string().trim().max(500).nullable().optional(),
})
    .refine((v) => v.enabled || (v.note != null && v.note.trim().length >= 5), {
    message: 'Zum Abschalten einer Regel ist eine Begründung (mind. 5 Zeichen) erforderlich.',
    path: ['note'],
});
// ============================================================================
// KI-Verbrauch
// ============================================================================
export const aiUsageBucket = z.object({
    key: z.string(),
    calls: z.number().int().nonnegative(),
    promptTokens: z.number().int().nonnegative(),
    completionTokens: z.number().int().nonnegative(),
    /** null, wenn keine Preise konfiguriert sind — dann zeigt das UI keine Kosten. */
    estimatedCostCents: z.number().nullable(),
});
export const aiUsageSummaryResponse = z.object({
    since: z.string().datetime(),
    total: aiUsageBucket,
    byRoute: z.array(aiUsageBucket),
    byUser: z.array(aiUsageBucket),
    hasCostEstimate: z.boolean(),
});
